// Same contract as /app/mobile/src/api.ts — same endpoints, same error copy, same token key.
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = "auth_token";

let token = null;
let onUnauthorized = null;
export const setToken = (t) => {
  token = t;
};
export const getToken = () => token;
export const setUnauthorizedHandler = (fn) => {
  onUnauthorized = fn;
};

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

export function errorText(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export async function api(path, opts = {}) {
  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method: opts.method ?? "GET",
      headers: {
        ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch {
    throw new ApiError("You're offline. Check your connection and try again.", 0);
  }
  if (res.status === 204) return undefined;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401 && token && !path.startsWith("/auth/")) onUnauthorized?.();
    throw new ApiError(errorText(data?.detail), res.status);
  }
  return data;
}

const CHUNK = 8 * 1024 * 1024;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Resumable chunked upload of a browser File into a project (8 MB chunks, each retried 3x).
 * Pass the previous uploadId to resume: chunks the server already has are skipped. Returns the created media.
 */
export async function chunkedUpload(projectId, file, onProgress, onUploadId, resumeId) {
  let state = null;
  if (resumeId) state = await api(`/uploads/${resumeId}`).catch(() => null);
  if (!state) {
    state = await api(`/projects/${projectId}/uploads`, {
      method: "POST",
      body: { filename: file.name, size: file.size, chunk_size: CHUNK },
    });
    onUploadId(state.upload_id);
  }
  const have = new Set(state.received);
  let done = state.received.length;
  onProgress(done / state.total_chunks);
  for (let i = 0; i < state.total_chunks; i++) {
    if (have.has(i)) continue;
    const body = file.slice(i * state.chunk_size, Math.min(file.size, (i + 1) * state.chunk_size));
    for (let attempt = 0; ; attempt++) {
      try {
        let res;
        try {
          res = await fetch(`${API}/uploads/${state.upload_id}/chunks/${i}`, {
            method: "PUT",
            body,
            headers: { "Content-Type": "application/octet-stream", Authorization: `Bearer ${token}` },
          });
        } catch {
          throw new ApiError("Upload failed", 0);
        }
        if (!res.ok) throw new ApiError(errorText((await res.json().catch(() => null))?.detail), res.status);
        break;
      } catch (e) {
        if (attempt >= 2 || (e instanceof ApiError && e.status >= 400 && e.status < 500)) throw e;
        await sleep(1000 * (attempt + 1));
      }
    }
    onProgress(++done / state.total_chunks);
  }
  return api(`/uploads/${state.upload_id}/complete`, { method: "POST" });
}

/** Multipart upload with real progress (XHR). Rejects with ApiError (status 0 = network). */
export function uploadFile(path, file, onProgress) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new ApiError(errorText(data?.detail), xhr.status));
    };
    xhr.onerror = () => reject(new ApiError("Upload failed", 0));
    xhr.send(form);
  });
}
